// The open project for React: a LiveProject made when the project's key is known, closed when the project changes.
// Edits are batched when the page is hidden or closed; closing with edits not yet stored asks the writer to wait.
import { useEffect, useState } from 'react';
import { flushWhenHidden } from '@writing-suite/client/net/outbox.ts';
import { addCloseTask } from '../host/host.ts';
import { isOnline } from './account.ts';
import { fetchProjectKey } from './sharing.ts';
import { roleOf } from './library.ts';
import { LiveProject, type ProjectState } from './project.ts';
import { startPresence } from './presence.ts';
import type { AppState } from '../shell/context.ts';

export interface OpenState extends ProjectState {
  /** The relay closed this project to this account (removed from it, or it left): the copy here reads only. */
  removed: boolean;
  /** Milliseconds from opening until the project could be shown. */
  readyMs: number | null;
  /** The live project, for edits (null until it's made). */
  live: LiveProject | null;
}

const initial: OpenState = {
  status: 'opening',
  project: null,
  version: 0,
  connection: 'closed',
  error: null,
  timings: { cacheMs: null, networkMs: null, cached: 0, fetched: 0 },
  save: null,
  epochNotice: null,
  readyMs: null,
  live: null,
  removed: false,
};

export function useLiveProject(app: AppState, projectId: string): OpenState & { missing: boolean } {
  const entry = app.projects?.find((p) => p.id === projectId) ?? null;
  const [state, setState] = useState<OpenState>(initial);

  useEffect(() => {
    if (!entry) return;
    const t0 = performance.now();
    let readyMs: number | null = null;
    // Comments and checkpoints are signed with the account's name (kept here for offline starts), else "Me".
    // A share the owner removed (or deleted) reads from this computer only, for exporting: no socket, no edits.
    const closedShare = !!entry.shared && entry.shared.status !== 'active';
    const deps: ConstructorParameters<typeof LiveProject>[2] = {
      api: app.account.session.api,
      store: app.account.store,
      online: closedShare ? () => false : isOnline,
      sessionEnded: app.sessionEnded,
      device: app.account.deviceId,
      memberId: app.account.accountId,
      author: authorName() ?? undefined,
      role: roleOf(entry),
      fetchKey: () => fetchProjectKey(app.account, entry.id),
      removed: () => {
        setState((s) => ({ ...s, removed: true }));
        void app.refreshProjects();
      },
    };
    const live = new LiveProject(entry.id, entry.key, deps, !closedShare);
    // Who's here (W9b): this device's caret out, the other members' in, over the project's socket.
    const presence = startPresence(live, { account: app.account.accountId, device: app.account.deviceId, name: () => deps.author });
    void app.account.session
      .account()
      .then((info) => {
        if (!info.name) return;
        deps.author = info.name;
        try {
          localStorage.setItem('ws:authorName', info.name);
        } catch {
          // this page only
        }
      })
      .catch(() => {});
    setState({ ...initial, live });
    live.subscribe((s) => {
      if (readyMs === null && s.status === 'ready') readyMs = Math.round(performance.now() - t0);
      setState((was) => ({ ...s, readyMs, live, removed: was.removed || closedShare }));
    });
    // Hidden or closed: what's typed is batched and stored at once. Closing before that's done asks to wait.
    let detach = () => {};
    const attach = () => {
      if (live.outbox) detach = flushWhenHidden(live.outbox, window, () => document.visibilityState === 'visible');
    };
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (!live.outbox?.hasUnsaved()) return;
      void live.flush();
      e.preventDefault();
    };
    window.addEventListener('beforeunload', beforeUnload);
    // A native shell closing its window asks first (host/host.ts): what's typed is stored before the count is read.
    const removeCloseTask = addCloseTask(() => live.flush());
    void live
      .open()
      .then(attach)
      .then(() => app.account.store.putInfo(entry.id, { lastOpened: Date.now() }))
      .then(() => app.noteProject(entry.id))
      .catch((e) => setState((s) => ({ ...s, status: s.project ? s.status : 'failed', error: e instanceof Error ? e.message : String(e) })));
    return () => {
      detach();
      window.removeEventListener('beforeunload', beforeUnload);
      removeCloseTask();
      presence?.stop();
      live.close();
    };
    // The key doesn't change for a project; its list entry is replaced whenever the Library refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.id]);

  return { ...state, missing: app.projects !== null && !entry };
}

/** The account's name as last seen (comments' author). */
export function authorName(): string | null {
  try {
    return localStorage.getItem('ws:authorName');
  } catch {
    return null;
  }
}
